import { Button, Button as MUIButton, Chip } from "@mui/joy";
import {
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { NerdSecurityGate } from "@nerdjs/nerd-core";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { ReactElement, SyntheticEvent, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { projectTab } from "../constants";
import { Task } from "../entities/task";
import Cues from "../hooks/cues/cues";
import Expenses from "../hooks/expenses/expenses";
import { useAppSelector } from "../hooks/hooks";
import Jobs from "../hooks/jobs/jobs";
import { MPSBatch } from "../hooks/musicPrepSummary/mpsBatch";
import MusicPrepSummary from "../hooks/musicPrepSummary/musicPrepSummary";
import OrchestratorMusicPrepSummary from "../hooks/musicPrepSummary/orchestratorMusicPrepSummary";
import { ProjectGraphs } from "../hooks/project/projectGraphs/projectGraphs";
import { ProjectTimers } from "../hooks/project/projectTimers/projectTimers";
import ProjectBudget from "../hooks/projectBudget/projectBudget";
import ProjectInfo from "../hooks/projectInfo/projectInfo";
import RouterBreadcrumbs from "../hooks/routerBreadcrumbs/routerBreadcrumbs";
import TalentLedger from "../hooks/talentLedger/talentLedger";
// import { queryBuilder } from "../network";
import {
  setDrawerOpen,
  setFormOpen,
  setTabIndex,
} from "../redux/appStatus/appStatusActions";
import {
  drawerOpenSelector,
  formOpenSelector,
  isAdminSelector,
  tabIndexSelector,
  userServicesSelector,
} from "../redux/appStatus/appStatusSelector";
import { useJobs } from "../redux/job/jobHooks";
import { useJobCategories } from "../redux/jobCategory/jobCategoryHooks";
import { usePositions } from "../redux/position/positionHooks";

import { useSelector } from "react-redux";
import { useCues } from "../redux/cue/cueHooks";
import {
  selectedProjectID,
  selectedVtransactionTalentID,
  setSelectedProjectID,
} from "../redux/filmsReducer";
import { useProject } from "../redux/project/projectHooks";
import { useProjectTasks } from "../redux/projectTask/projectTaskHooks";
import { useScales } from "../redux/scale/scaleHooks";
import { useTalents } from "../redux/talent/talentHooks";
import { useTaskTypes } from "../redux/taskType/taskTypeHooks";
import { useUpdateBatchTasksMutation } from "../redux/task/taskEndpoints";

/**
 *
 * @returns {ReactElement} Nerd route
 */
export default function ProjectRoute(): ReactElement {
  const tabIndex = useAppSelector(tabIndexSelector(projectTab)) ?? 0;
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectID = parseInt(params.id ?? "");

  const vtransactionTalentID = useAppSelector(selectedVtransactionTalentID);
  const userServices = useAppSelector(userServicesSelector);
  const drawerOpen = useAppSelector(drawerOpenSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const { cues } = useCues({
    filters: JSON.stringify([
      {
        name: "cues.projectID",
        value: projectID,
        comparison: "eq",
      },
    ]),
  });
  const budgetOpen = useAppSelector(formOpenSelector("budget"));

  const { jobs, isLoading: l1 } = useJobs(
    {
      filters: JSON.stringify([
        {
          name: "jobs.projectID",
          value: projectID,
          comparison: "eq",
        },
      ]),
    },
    { skip: !projectID }
  );
  const { talents, isLoading: l2 } = useTalents();
  const { jobCategories, isLoading: l3 } = useJobCategories();
  const { taskTypes, isLoading: l4 } = useTaskTypes();
  const { positions, isLoading: l5 } = usePositions();
  const { scales, isLoading: l6 } = useScales();
  const { project, isLoading: l7 } = useProject(projectID);
  const { projectTasks } = useProjectTasks(projectID);
  const allotmentJournalEntrySize = JSON.parse(
    localStorage.getItem("films_allotmentJournalEntrySize") ?? "[]"
  );
  const defaultLedgerSplit =
    Array.isArray(allotmentJournalEntrySize) &&
    allotmentJournalEntrySize.length === 2
      ? allotmentJournalEntrySize
      : [50, 50];

  const isLoading = l1 || l2 || l3 || l4 || l5 || l6 || l7;

  useEffect(() => {
    setSearchParams((prev) => {
      prev.set("talentID", vtransactionTalentID?.toString() ?? "");
      return prev;
    });
  }, [vtransactionTalentID]);

  const handleTabIndexChange = (
    e: SyntheticEvent<Element, Event>,
    v: number
  ) => {
    dispatch(setTabIndex(projectTab, v));
  };

  useEffect(() => {
    dispatch(setSelectedProjectID(projectID));
    const tab = searchParams.get("tab");
    if (tab) {
      dispatch(setTabIndex(projectTab, parseInt(tab)));
    }

    return () => {
      dispatch(setTabIndex(projectTab, 0));
      // dispatch(setSelectedGridCues([]));
      dispatch(setSelectedProjectID());
      // dispatch(setBulkSelect(false));
      // dispatch(setTaskBulkSelect(false));
      // dispatch(setSelectedGridTasks([]));
    };
  }, []);

  function getTabContent() {
    switch (tabIndex) {
      case 0:
        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              p: 2,
              width: "100%",
            }}
          >
            <Cues />
          </Box>
        );
      case 1:
        return (
          <Box
            sx={{ display: "flex", flexGrow: 1, gap: 1, p: 2, width: "100%" }}
            key="copyists"
          >
            {vtransactionTalentID ? (
              <Allotment
                defaultSizes={defaultLedgerSplit}
                onChange={(sizes) =>
                  localStorage.setItem(
                    "films_allotmentJournalEntrySize",
                    JSON.stringify(sizes)
                  )
                }
              >
                <Box sx={{ display: "flex", flex: 1, minWidth: 0, pr: 0.5 }}>
                  <Jobs />
                </Box>
                <Box sx={{ display: "flex", flex: 1, minWidth: 0, pl: 0.5 }}>
                  <TalentLedger talentID={vtransactionTalentID} />
                </Box>
              </Allotment>
            ) : (
              <Jobs />
            )}
          </Box>
        );

      case 2:
        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              gap: 1,
              width: "100%",
              p: 2,
            }}
            key="orchestrator"
          >
            {vtransactionTalentID ? (
              <Allotment
                defaultSizes={defaultLedgerSplit}
                onChange={(sizes) =>
                  localStorage.setItem(
                    "films_allotmentJournalEntrySize",
                    JSON.stringify(sizes)
                  )
                }
              >
                <Box sx={{ display: "flex", flex: 1, minWidth: 0, pr: 0.5 }}>
                  <Jobs orchestrator />
                </Box>
                <Box sx={{ display: "flex", flex: 1, minWidth: 0, pl: 0.5 }}>
                  <TalentLedger talentID={vtransactionTalentID} />
                </Box>
              </Allotment>
            ) : (
              <Jobs orchestrator />
            )}
          </Box>
        );

      case 3:
        const orchestrationCategory = jobCategories?.[2];
        const copyistsCategory = jobCategories?.[1];
        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              flexDirection: "column",
              p: 2,
              width: "100%",
            }}
          >
            <Box sx={{ height: 162, display: "flex", flexDirection: "column" }}>
              <Typography variant="overline">
                {orchestrationCategory?.name ?? ""}
              </Typography>
              <MusicPrepSummary
                projectTask={projectTasks?.find(
                  (p) => p.id === orchestrationCategory?.id
                )}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 336px)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1,
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="overline">
                    {copyistsCategory?.name ?? ""}
                  </Typography>
                  <MUIButton
                    size="sm"
                    variant="soft"
                    onClick={() => dispatch(setFormOpen(true, "mpsBatch"))}
                    disabled={project?.locked()}
                  >
                    <i className="fa-solid fa-line-columns pright"></i> Batch
                    Import
                  </MUIButton>
                  <MPSBatch />
                </Box>
                <ReconcileRates />
              </Box>
              <MusicPrepSummary
                projectTask={projectTasks?.find(
                  (p) => p.id === copyistsCategory?.id
                )}
              />
            </Box>
          </Box>
        );
      case 4:
        const orchestratorsCategory = jobCategories?.[0];
        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              flexDirection: "column",
              p: 2,
              width: "100%",
            }}
          >
            <OrchestratorMusicPrepSummary
              projectTask={projectTasks?.find(
                (p) => p.id === orchestratorsCategory?.id
              )}
            />
          </Box>
        );

      case 5:
        return (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              p: 2,
              width: "100%",
            }}
          >
            <Expenses />
          </Box>
        );
      case 6:
        return (
          <Box sx={{ display: "flex", flexGrow: 1, p: 2, width: "100%" }}>
            <ProjectTimers />
          </Box>
        );

      case 7:
        return (
          <Box
            sx={{
              overflow: "scroll",
              width: "100%",
              height: "calc(100vh - 136px)",
              position: "relative",
            }}
          >
            <ProjectInfo />
          </Box>
        );
      case 8:
        return (
          <Box
            sx={{
              overflow: "scroll",
              width: "100%",
              height: "calc(100vh - 136px)",
              position: "relative",
            }}
          >
            <ProjectGraphs />
          </Box>
        );

      default:
        return <div>Coming very soon 🙂</div>;
    }
  }

  const loadingProgress = (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );

  if (isLoading) {
    return loadingProgress;
  }

  const handleAllotmentChange = (e: number[]) => {
    if (e.length === 2)
      localStorage.setItem(
        "films_allotmentJournalEntrySize",
        JSON.stringify(e)
      );
  };

  const adminTabs = [
    <Tab value={0} key="cues" label="Cues" />,
    <Tab value={1} key="jobs" label="Copyists" />,
    <Tab value={2} key="jobs" label="Orchestrators" />,
    <Tab
      value={4}
      key="Orchestration"
      label="Orchestration"
      disabled={cues?.length === 0 || jobs?.length === 0}
    />,
    <Tab
      value={3}
      key="mps"
      label="Music Prep Summary"
      disabled={cues?.length === 0 || jobs?.length === 0}
    />,
    <Tab value={5} key="Expenses" label="Expenses" />,
    <Tab value={6} key="Timers" label="Timers" />,
    // <Tab value={8} key="Graphs" label="Graphs" />,
    <Tab value={7} key="Project" label="Project" />,
  ];
  const limitedTabs = [
    <Tab value={0} key="cues" label="Cues" />,
    <Tab value={5} key="Expenses" label="Expenses" />,
  ];

  return (
    <Allotment
      vertical={false}
      onChange={handleAllotmentChange}
      defaultSizes={allotmentJournalEntrySize}
    >
      <Allotment.Pane minSize={300}>
        <Box
          sx={{
            height: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              position: "sticky",
              borderRadius: 0,
              zIndex: 99,
            }}
          >
            <Box sx={{ p: 1 }}>
              <RouterBreadcrumbs
                onMoveBack={() => dispatch(setSelectedProjectID())}
                last={project?.name}
              />
            </Box>
            <Tabs
              variant="scrollable"
              value={tabIndex}
              onChange={handleTabIndexChange}
              aria-label="basic tabs example"
            >
              {isAdmin ? adminTabs : limitedTabs}
            </Tabs>
            <NerdSecurityGate id="button-budget" userServices={userServices}>
              <Button
                variant="solid"
                color="neutral"
                onClick={() => {
                  dispatch(setFormOpen(!budgetOpen, "budget"));
                  if (!budgetOpen && drawerOpen) dispatch(setDrawerOpen(false));
                }}
                sx={{
                  position: "absolute",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  top: 10,
                  right: 0,
                  zIndex: 999,
                }}
                size="sm"
                endDecorator={
                  <i
                    className={`fa-sharp fa-solid fa-chevron-${
                      budgetOpen ? "left" : "right"
                    } pleft`}
                  ></i>
                }
              >
                Budget
              </Button>
            </NerdSecurityGate>
          </Paper>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              maxHeight: "calc(100vh - 136px)",
            }}
          >
            {getTabContent()}
          </Box>
        </Box>
      </Allotment.Pane>
      {budgetOpen ? (
        <Allotment.Pane
          minSize={320}
          preferredSize={
            allotmentJournalEntrySize.length === 2
              ? allotmentJournalEntrySize[1]
              : 420
          }
        >
          <Box
            sx={{
              height: "calc(100vh - 48px)",
              display: "flex",
              overflow: "scroll",
            }}
          >
            <ProjectBudget />
          </Box>
        </Allotment.Pane>
      ) : (
        []
      )}
    </Allotment>
  );
}

function ReconcileRates() {
  const { scalesMap } = useScales();

  const projectID = useSelector(selectedProjectID);
  const { projectTasks } = useProjectTasks(projectID ?? 0);
  const copyists = projectTasks?.length ? projectTasks[1] : undefined;
  const [updateBatchTasks] = useUpdateBatchTasksMutation();

  const allTasks: Task[] = [];
  copyists?.cues.forEach((c) => {
    c.tasks.forEach((t) => {
      allTasks.push(t);
    });
  });

  const handleClick = () => {
    const body: Partial<Task>[] = [];
    allTasks.forEach((t) => {
      const _t = { ...t };
      _t.rate = scalesMap?.[t.scaleID]?.rate ?? t.rate;
      body.push(_t);
    });

    updateBatchTasks(body);
  };

  return (
    <Tooltip title="This action will apply the latest rates to the Music Prep Summary.">
      <Chip
        startDecorator={<i className="fa-solid fa-arrows-rotate"></i>}
        onClick={handleClick}
        variant="soft"
        size="sm"
        color="neutral"
      >
        Use latest rates
      </Chip>
    </Tooltip>
  );
}
